/**
 * Canvas overlay rendering angle visualization between three keypoints.
 *
 * Draws two lines from the vertex through each endpoint (with configurable offset),
 * a filled arc at the minimum arm length, and a label with leader-line + underline
 * matching the POC style.
 */

import type { Vec3 } from '../../domain';
import type { IViewport } from '../../viewports';
import type { IOverlayObject } from '../types';

export interface AngleOverlayConfig {
    readonly id: string;
    readonly vertex: Vec3;
    readonly pointA: Vec3;
    /**
     * Second arm endpoint. Null renders a single arm (pointA → vertex) only —
     * used for the in-progress angle preview before the third point is placed.
     */
    readonly pointB: Vec3 | null;
    readonly angleDegrees: number;
    readonly measurementName?: string;
    readonly color?: string;
    readonly lineOffset?: number;
    readonly isLongestArcMode?: boolean;
    /** Draw the value/leader label. Defaults to true; set false for previews. */
    readonly showLabel?: boolean;
}

export class AngleOverlay implements IOverlayObject {
    readonly id: string;
    readonly zIndex = 200;
    readonly interactive = false;
    readonly clipByCamera = false;
    visible = true;

    private vertex: Vec3;
    private pointA: Vec3;
    private pointB: Vec3 | null;
    private angleDegrees: number;
    private measurementName: string;
    private color: string;
    private lineOffset: number;
    private isLongestArcMode: boolean;
    private showLabel: boolean;

    private readonly labelOffset = 80;
    private readonly font = "14px 'JetBrains Mono', monospace";
    private readonly lineWidth = 1.5;

    constructor(config: AngleOverlayConfig) {
        this.id = config.id;
        this.vertex = config.vertex;
        this.pointA = config.pointA;
        this.pointB = config.pointB;
        this.angleDegrees = config.angleDegrees;
        this.measurementName = config.measurementName ?? '';
        this.color = config.color ?? 'rgba(255, 255, 0, 1.0)';
        this.lineOffset = config.lineOffset ?? 2.0;
        this.isLongestArcMode = config.isLongestArcMode ?? true;
        this.showLabel = config.showLabel ?? true;
    }

    updateAngle(vertex: Vec3, pointA: Vec3, pointB: Vec3 | null, angleDegrees: number): void {
        this.vertex = vertex;
        this.pointA = pointA;
        this.pointB = pointB;
        this.angleDegrees = angleDegrees;
    }

    hitTest(_canvasX: number, _canvasY: number): boolean {
        return false;
    }

    onCameraChanged(_viewport: IViewport): void {
        // Canvas positions are updated in render()
    }

    render(ctx: CanvasRenderingContext2D, viewport: IViewport): void {
        // Single-arm preview: only pointA → vertex is known yet.
        if (this.pointB == null) {
            this.renderArm(ctx, viewport);
            return;
        }

        const overlayLayer = viewport.getOverlayLayer();
        const canvasWidth = overlayLayer.getCssWidth();

        const geom = this.calculateGeometry();

        // Project all points to canvas
        const projLine1Start = viewport.worldToCanvas(geom.line1Start);
        const projLine1End = viewport.worldToCanvas(geom.line1End);
        const projLine2Start = viewport.worldToCanvas(geom.line2Start);
        const projLine2End = viewport.worldToCanvas(geom.line2End);
        const projVertex = viewport.worldToCanvas(this.vertex);
        const projText = viewport.worldToCanvas(geom.textPosition);

        if (!projVertex.visible) return;

        ctx.save();

        // Shadow for all elements
        ctx.shadowColor = 'rgba(0, 0, 0, 0.5)';
        ctx.shadowBlur = 2;
        ctx.shadowOffsetX = 1;
        ctx.shadowOffsetY = 1;

        ctx.strokeStyle = this.color;
        ctx.fillStyle = this.color;
        ctx.lineWidth = this.lineWidth;

        // Line 1: pointA arm through vertex
        ctx.beginPath();
        ctx.moveTo(projLine1Start.x, projLine1Start.y);
        ctx.lineTo(projLine1End.x, projLine1End.y);
        ctx.stroke();

        // Line 2: pointB arm extended beyond vertex
        ctx.beginPath();
        ctx.moveTo(projLine2Start.x, projLine2Start.y);
        ctx.lineTo(projLine2End.x, projLine2End.y);
        ctx.stroke();

        // Arc sampled in world space then projected, so it stays anchored to both
        // arms under perspective (3D) projection — not just one arm's screen radius.
        const arcPoints = this.sampleArcCanvasPoints(geom, viewport);
        if (arcPoints.length >= 2) {
            // Filled wedge: vertex → arc samples → close
            ctx.beginPath();
            ctx.moveTo(projVertex.x, projVertex.y);
            for (const p of arcPoints) {
                ctx.lineTo(p.x, p.y);
            }
            ctx.closePath();
            ctx.fillStyle = this.colorWithAlpha(0.3);
            ctx.fill();

            // Arc outline (polyline through the samples, not back to the vertex)
            ctx.strokeStyle = this.color;
            ctx.beginPath();
            ctx.moveTo(arcPoints[0].x, arcPoints[0].y);
            for (let i = 1; i < arcPoints.length; i++) {
                ctx.lineTo(arcPoints[i].x, arcPoints[i].y);
            }
            ctx.stroke();
        }

        // --- Label with leader line + underline ---
        if (!this.showLabel || !projText.visible) {
            ctx.restore();
            return;
        }

        const labelOnRight = projText.x < canvasWidth / 2;

        const valueText = `${this.angleDegrees.toFixed(1)}\u00B0`;
        const legend = this.measurementName ? `${this.measurementName}: ${valueText}` : valueText;

        ctx.font = this.font;
        const metrics = ctx.measureText(legend);
        const textWidth = metrics.width;

        const labelX = labelOnRight
            ? projText.x + this.labelOffset
            : projText.x - this.labelOffset - textWidth;
        const labelY = projText.y;

        const anchorX = labelOnRight ? labelX : labelX + textWidth;

        ctx.strokeStyle = this.color;
        ctx.fillStyle = this.color;

        // Dashed leader line from text position to label anchor
        ctx.setLineDash([6, 4]);
        ctx.beginPath();
        ctx.moveTo(projText.x, projText.y);
        ctx.lineTo(anchorX, labelY);
        ctx.stroke();

        // Solid horizontal underline
        ctx.setLineDash([]);
        const underlineEndX = labelOnRight ? labelX + textWidth : labelX;
        ctx.beginPath();
        ctx.moveTo(anchorX, labelY);
        ctx.lineTo(underlineEndX, labelY);
        ctx.stroke();

        // Text above the underline
        ctx.textAlign = labelOnRight ? 'left' : 'right';
        ctx.textBaseline = 'bottom';
        const textX = labelOnRight ? labelX : labelX + textWidth;

        ctx.shadowColor = 'rgba(0, 0, 0, 0.7)';
        ctx.shadowBlur = 2;
        ctx.shadowOffsetX = 1;
        ctx.shadowOffsetY = 1;

        ctx.fillText(legend, textX, labelY - 2);

        ctx.restore();
    }

    /**
     * Render a single arm (pointA → vertex) for the in-progress preview, before
     * the second arm endpoint is placed.
     */
    private renderArm(ctx: CanvasRenderingContext2D, viewport: IViewport): void {
        const projA = viewport.worldToCanvas(this.pointA);
        const projV = viewport.worldToCanvas(this.vertex);
        if (!projA.visible && !projV.visible) return;

        ctx.save();
        ctx.shadowColor = 'rgba(0, 0, 0, 0.5)';
        ctx.shadowBlur = 2;
        ctx.shadowOffsetX = 1;
        ctx.shadowOffsetY = 1;

        ctx.strokeStyle = this.color;
        ctx.lineWidth = this.lineWidth;
        ctx.beginPath();
        ctx.moveTo(projA.x, projA.y);
        ctx.lineTo(projV.x, projV.y);
        ctx.stroke();

        ctx.restore();
    }

    // ========================================================================
    // Geometry calculation (world space)
    // ========================================================================

    private calculateGeometry(): AngleGeometry {
        const pointB = this.pointB ?? this.vertex;
        const p2p1 = vec3Sub(this.pointA, this.vertex);
        const p2p3 = vec3Sub(pointB, this.vertex);

        const dirP2P1 = vec3Normalize(p2p1);
        const dirP2P3 = vec3Normalize(p2p3);

        const lenP2P1 = vec3Length(p2p1);
        const lenP2P3 = vec3Length(p2p3);
        const minLen = Math.min(lenP2P1, lenP2P3);

        // Line 1: from (pointA + offset along direction) to vertex
        const line1Start = vec3Add(this.pointA, vec3Scale(dirP2P1, this.lineOffset));
        const line1End = this.vertex;

        // Line 2: from (pointB + offset) to (beyond vertex in opposite direction)
        const line2Start = vec3Add(this.vertex, vec3Scale(dirP2P3, lenP2P3 + this.lineOffset));
        const extensionLen = this.isLongestArcMode ? minLen + this.lineOffset : this.lineOffset;
        const line2End = vec3Add(this.vertex, vec3Scale(dirP2P3, -extensionLen));

        // Arc endpoints at minLen from vertex
        const arcP1 = vec3Add(this.vertex, vec3Scale(dirP2P1, minLen));
        const arcP2Dir = this.isLongestArcMode ? vec3Scale(dirP2P3, -1) : dirP2P3;
        const arcP2 = vec3Add(this.vertex, vec3Scale(arcP2Dir, minLen));

        // Bisector for text position
        const arcV1 = vec3Sub(arcP1, this.vertex);
        const arcV2 = vec3Sub(arcP2, this.vertex);
        const bisecSign = this.isLongestArcMode ? -1.0 : 1.0;
        const bisec = vec3Scale(vec3Scale(vec3Add(arcV1, arcV2), 0.5), bisecSign);
        const bisecNorm = vec3Normalize(bisec);
        const textPosition = vec3Add(this.vertex, vec3Scale(bisecNorm, minLen * -0.5));

        return {
            line1Start,
            line1End,
            line2Start,
            line2End,
            arcP1,
            arcP2,
            textPosition,
            // Unit directions of the arc endpoints and the common arc radius, so
            // render() can sample the arc in world space (correct under perspective).
            dirStart: dirP2P1,
            dirEnd: arcP2Dir,
            minLen,
        };
    }

    // ========================================================================
    // World-space arc sampling
    // ========================================================================

    /**
     * Sample the arc in world space (sweeping between the two arm directions at a
     * constant world radius) and project each sample to canvas. Because every
     * point is the true projection of a point on the arc, the result stays
     * anchored to both arms under perspective projection — unlike a single-radius
     * screen-space circle. Returns [] when the arms are collinear (degenerate).
     */
    private sampleArcCanvasPoints(geom: AngleGeometry, viewport: IViewport): Array<{ x: number; y: number }> {
        // In-plane orthonormal basis: dir(phi) = cos(phi)·u + sin(phi)·w, with w
        // oriented toward dirEnd so the sweep traces the minor arc directly from
        // dirStart (arcP1) to dirEnd (arcP2). The longest-arc styling is already
        // baked into dirEnd by calculateGeometry (arcP2 = -dirP2P3); we always
        // draw the short arc between the two endpoints, matching the prior look.
        const u = geom.dirStart;
        const n = vec3Normalize(vec3Cross(geom.dirStart, geom.dirEnd));
        if (vec3Length(n) < 1e-6) return []; // arms collinear — nothing to draw
        const w = vec3Normalize(vec3Cross(n, u));

        const cosT = Math.max(-1, Math.min(1, vec3Dot(geom.dirStart, geom.dirEnd)));
        const sweep = Math.acos(cosT);

        const steps = 48;
        const points: Array<{ x: number; y: number }> = [];
        for (let i = 0; i <= steps; i++) {
            const phi = (sweep * i) / steps;
            const cos = Math.cos(phi);
            const sin = Math.sin(phi);
            const dir: Vec3 = [u[0] * cos + w[0] * sin, u[1] * cos + w[1] * sin, u[2] * cos + w[2] * sin];
            const world = vec3Add(this.vertex, vec3Scale(dir, geom.minLen));
            const proj = viewport.worldToCanvas(world);
            points.push({ x: proj.x, y: proj.y });
        }
        return points;
    }

    // ========================================================================
    // Color helper
    // ========================================================================

    private colorWithAlpha(alpha: number): string {
        // Handle rgba(...) format
        const rgbaMatch = this.color.match(/rgba?\(\s*([\d.]+)\s*,\s*([\d.]+)\s*,\s*([\d.]+)/);
        if (rgbaMatch) {
            return `rgba(${rgbaMatch[1]}, ${rgbaMatch[2]}, ${rgbaMatch[3]}, ${alpha})`;
        }
        // Fallback
        return `rgba(255, 255, 0, ${alpha})`;
    }
}

// ============================================================================
// Vec3 helpers (local, no allocation overhead from class instances)
// ============================================================================

interface AngleGeometry {
    line1Start: Vec3;
    line1End: Vec3;
    line2Start: Vec3;
    line2End: Vec3;
    arcP1: Vec3;
    arcP2: Vec3;
    textPosition: Vec3;
    /** Unit direction from the vertex toward the first arc endpoint (arcP1). */
    dirStart: Vec3;
    /** Unit direction from the vertex toward the second arc endpoint (arcP2). */
    dirEnd: Vec3;
    /** World-space arc radius (the shorter arm length). */
    minLen: number;
}

function vec3Sub(a: Vec3 | ReadonlyArray<number>, b: Vec3 | ReadonlyArray<number>): Vec3 {
    return [a[0] - b[0], a[1] - b[1], a[2] - b[2]];
}

function vec3Add(a: Vec3 | ReadonlyArray<number>, b: Vec3 | ReadonlyArray<number>): Vec3 {
    return [a[0] + b[0], a[1] + b[1], a[2] + b[2]];
}

function vec3Scale(v: Vec3 | ReadonlyArray<number>, s: number): Vec3 {
    return [v[0] * s, v[1] * s, v[2] * s];
}

function vec3Length(v: Vec3 | ReadonlyArray<number>): number {
    return Math.sqrt(v[0] * v[0] + v[1] * v[1] + v[2] * v[2]);
}

function vec3Dot(a: Vec3 | ReadonlyArray<number>, b: Vec3 | ReadonlyArray<number>): number {
    return a[0] * b[0] + a[1] * b[1] + a[2] * b[2];
}

function vec3Cross(a: Vec3 | ReadonlyArray<number>, b: Vec3 | ReadonlyArray<number>): Vec3 {
    return [a[1] * b[2] - a[2] * b[1], a[2] * b[0] - a[0] * b[2], a[0] * b[1] - a[1] * b[0]];
}

function vec3Normalize(v: Vec3 | ReadonlyArray<number>): Vec3 {
    const len = vec3Length(v);
    if (len === 0) return [0, 0, 0];
    return [v[0] / len, v[1] / len, v[2] / len];
}
